<template>
  <div class="crud_form_wrapper">
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.add_task") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #446e80">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>

    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.taskNameAr')"
            v-model.trim="data.name_ar"
            :maxlength="50"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.taskNameEn')"
            v-model.trim="data.name_en"
            :maxlength="50"
            required
          />

          <base-picker-input
            col="6"
            type="date"
            :placeholder="$t('PLACEHOLDERS.startDate')"
            v-model="data.start_at"
            required
          />

          <base-picker-input
            col="6"
            type="date"
            :placeholder="$t('PLACEHOLDERS.endDate')"
            v-model="data.end_at"
            required
          />

          <base-select-input
            col="6"
            :optionsList="activeStatuses"
            :placeholder="$t('PLACEHOLDERS.status')"
            v-model="data.is_active"
            required
          />

          <base-select-input
            col="6"
            :optionsList="facilities"
            :placeholder="$t('PLACEHOLDERS.facilityName')"
            v-model="data.facility"
            required
          />

          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.taskInstructions')"
            v-model.trim="data.instructions"
            :maxlength="500"
          />

          <div class="col-12 mt-2 mb-2">
            <h6>{{ $t("PLACEHOLDERS.taskEmployeeSchedule") }}</h6>
          </div>

          <div
            class="col-12"
            v-for="(item, index) in breaks"
            :key="`break-${index}`"
          >
            <div class="row">
              <base-select-input
                col="4"
                :optionsList="getEmployeesOptionsForRow(index)"
                :placeholder="$t('PLACEHOLDERS.employeeName')"
                v-model="item.employee"
              />

              <base-picker-input
                col="3"
                type="time"
                :placeholder="$t('PLACEHOLDERS.workStartTime')"
                v-model="item.work_start_time"
              />

              <base-picker-input
                col="3"
                type="time"
                :placeholder="$t('PLACEHOLDERS.workEndTime')"
                v-model="item.work_end_time"
              />

              <div class="col-1 d-flex align-center justify-center">
                <button
                  type="button"
                  class="break_delete_btn"
                  @click="removeBreak(index)"
                >
                  <i class="fal fa-trash-alt"></i>
                </button>
              </div>
            </div>

            <div class="row mt-1">
              <div class="col-12 mb-1">
                <small class="optional_break_label">{{
                  $t("PLACEHOLDERS.breakTimesOptional")
                }}</small>
              </div>

              <base-picker-input
                col="3"
                type="time"
                :placeholder="$t('PLACEHOLDERS.breakStartTime')"
                v-model="item.break_start_time"
              />

              <base-picker-input
                col="3"
                type="time"
                :placeholder="$t('PLACEHOLDERS.breakEndTime')"
                v-model="item.break_end_time"
              />
            </div>
          </div>

          <div class="col-12 mb-2">
            <button type="button" class="add_break_btn" @click="addBreak">
              {{ $t("BUTTONS.add") }}
            </button>
          </div>

          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { appendEmployeeScheduleToFormData } from "@/utils/taskScheduleUtils";

export default {
  name: "CreateTask",

  data() {
    return {
      isWaitingRequest: false,
      data: {
        name_ar: null,
        name_en: null,
        start_at: null,
        end_at: null,
        is_active: null,
        instructions: null,
        facility: null,
      },
      facilities: [],
      employees: [],
      breaks: [
        {
          employee: null,
          work_start_time: null,
          work_end_time: null,
          break_start_time: null,
          break_end_time: null,
        },
      ],
    };
  },

  computed: {
    activeStatuses() {
      return [
        { id: 1, name: this.$t("STATUS.active"), value: 1 },
        { id: 2, name: this.$t("STATUS.notActive"), value: 0 },
      ];
    },
  },

  methods: {
    addBreak() {
      this.breaks.push({
        employee: null,
        work_start_time: null,
        work_end_time: null,
        break_start_time: null,
        break_end_time: null,
      });
    },

    getEmployeesOptionsForRow(currentIndex) {
      const selectedIds = this.breaks
        .map((item, index) =>
          index !== currentIndex ? item.employee?.value : null
        )
        .filter(Boolean);

      return this.employees.filter((employee) => {
        const currentEmployeeId = this.breaks[currentIndex]?.employee?.value;
        return (
          employee.value === currentEmployeeId ||
          !selectedIds.includes(employee.value)
        );
      });
    },

    hasDuplicateEmployees() {
      const selectedIds = this.breaks
        .map((item) => item.employee?.value)
        .filter(Boolean);

      return new Set(selectedIds).size !== selectedIds.length;
    },

    removeBreak(index) {
      if (this.breaks.length === 1) return;
      this.breaks.splice(index, 1);
    },

    async getFacilities() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "facilities",
          params: { page: 0, limit: 0, isActive: 1, ignorePermissionCheck: 1 },
        });

        this.facilities = (res.data.data.data || []).map((facility) => ({
          id: facility.id,
          name: facility.name,
          value: facility.id,
        }));
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },

    async getEmployees() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "clients",
          params: { page: 0, limit: 0, isActive: 1, ignorePermissionCheck: 1 },
        });

        this.employees = (res.data.data.data || []).map((employee) => ({
          id: employee.id,
          name: employee.name,
          value: employee.id,
        }));
      } catch (error) {
        try {
          const fallback = await this.$axios({
            method: "GET",
            url: "clients",
            params: { page: 0, limit: 0, isActive: 1, ignorePermissionCheck: 1 },
          });

          this.employees = (fallback.data.data.data || []).map((employee) => ({
            id: employee.id,
            name: employee.name,
            value: employee.id,
          }));
        } catch (fallbackError) {
          console.log(
            fallbackError.response?.data?.message || fallbackError.message
          );
        }
      }
    },

    validateFormInputs() {
      this.isWaitingRequest = true;

      if (!this.data.facility?.value) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.facility"));
        return;
      }

      if (!this.data.is_active) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.status"));
        return;
      }

      if (this.hasDuplicateEmployees()) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.employee") || "Employee selected twice"
        );
        return;
      }

      this.submitForm();
    },

    async submitForm() {
      const REQUEST_DATA = new FormData();

      REQUEST_DATA.append("name[ar]", this.data.name_ar);
      REQUEST_DATA.append("name[en]", this.data.name_en);
      REQUEST_DATA.append("start_at", this.data.start_at);
      REQUEST_DATA.append("end_at", this.data.end_at);
      REQUEST_DATA.append("is_active", this.data.is_active.value);
      REQUEST_DATA.append("instructions", this.data.instructions || "");
      REQUEST_DATA.append("facility_id", this.data.facility.value);

      appendEmployeeScheduleToFormData(REQUEST_DATA, this.breaks);

      try {
        await this.$axios({ method: "POST", url: `tasks`, data: REQUEST_DATA });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push({ path: "/tasks/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response?.data?.message || error.message);
      }
    },
  },

  async created() {
    this.data.is_active = this.activeStatuses[0];
    await this.getFacilities();
    await this.getEmployees();
  },
};
</script>

<style scoped>
.break_delete_btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #fbe9e7;
  color: #d84315;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add_break_btn {
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  background: #446e80;
  color: #fff;
  font-size: 14px;
}

.optional_break_label {
  color: var(--theme_text_clr);
  opacity: 0.75;
}
</style>
